import { createSelector } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { AudioLinesIcon, BedIcon, BrainIcon, LucideIcon, SnailIcon, TreesIcon } from 'lucide-preact';
import { ComponentProps } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { AppState, useSelector } from '@/redux/helper';
import { AmbientModeBackgroundMusic } from '@/redux/slices/remoteConfig/types';
import { setLastSelectedBackgroundMusicURL } from '@/redux/slices/settings/ambient/action';

const LUCIDE_ICONS: Record<string, LucideIcon> = {
  AudioLinesIcon,
  BedIcon,
  BrainIcon,
  SnailIcon,
  TreesIcon,
};

interface BackgroundMusicSelectorProps extends ComponentProps<'div'> {
  muted: boolean;
  volume: number;
  isExpanded?: boolean;
}

const BackgroundMusicSelector = ({
  muted,
  volume,
  isExpanded = false,
  className,
  ...props
}: BackgroundMusicSelectorProps) => {
  const dispatch = useDispatch();
  const lastSelectedBackgroundMusicURL = useSelector((state) => state.settings.ambientMode.lastSelectedBackgroundMusicURL);
  const selectMusics = useMemo(
    () =>
      createSelector(
        (state: AppState) => state.remoteConfig.ambientModeBackgroundMusics,
        (_: AppState, targetURL: string | undefined) => targetURL,
        (musics, targetURL) => {
          if (!targetURL) {
            return [musics[0], musics.slice(1)] as [AmbientModeBackgroundMusic | undefined, AmbientModeBackgroundMusic[]];
          }
          const result = musics.reduce(
            (acc, item) => {
              if (item.url === targetURL) {
                acc[0] = item;
              } else {
                acc[1].push(item);
              }
              return acc;
            },
            [undefined, []] as [AmbientModeBackgroundMusic | undefined, AmbientModeBackgroundMusic[]],
          );
          if (!result[0]) {
            return [musics[0], musics.slice(1)] as [AmbientModeBackgroundMusic | undefined, AmbientModeBackgroundMusic[]];
          }
          return result;
        },
      ),
    [],
  );
  const [selectedMusicURL, setSelectedMusicURL] = useState(lastSelectedBackgroundMusicURL);
  const [selectedMusic, otherMusics] = useSelector((state) => selectMusics(state, selectedMusicURL));

  const audioRef = useRef<HTMLAudioElement>(null);
  const safeVolume = Math.max(0, Math.min(1, volume));

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!muted && selectedMusic?.url && audioElement) {
      audioElement.volume = safeVolume;
      audioElement.play().catch((error) => {
        console.error('Failed to play background music:', error);
      });
    }

    return () => {
      if (audioElement) {
        audioElement.pause();
      }
    };
  }, [muted, selectedMusic?.url, safeVolume]);

  return (
    <div
      className={clsx(
        'flex flex-col items-start gap-[10px]',
        isExpanded ? 'pointer-events-auto' : 'pointer-events-none',
        className,
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <audio ref={audioRef} src={selectedMusic?.url} loop className="hidden" />
      <ul
        className={clsx(
          'preflight preflight-ul flex flex-col gap-[10px] transition-opacity',
          isExpanded ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        {otherMusics.map((music) => {
          const Icon = LUCIDE_ICONS[music.icon];
          return (
            <li key={music.url}>
              <button
                type="button"
                className="preflight preflight-button flex h-8 items-center gap-[7px] rounded-full bg-white/60 px-[15px] py-1 backdrop-blur-[3px]"
                onClick={() => {
                  setSelectedMusicURL(music.url);
                  dispatch(setLastSelectedBackgroundMusicURL(music.url));
                }}
              >
                {Icon && <Icon className="size-5" />}
                <span className="text-sm font-bold leading-normal text-black">{music.name}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {selectedMusic && (
        <div
          className={clsx(
            'flex h-8 cursor-default items-center gap-[7px] rounded-full bg-[#265832] px-[15px] py-1 transition-opacity',
            isExpanded ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <AudioLinesIcon className="size-5 text-white" />
          <span className="text-sm font-bold leading-normal text-white">{selectedMusic.name}</span>
        </div>
      )}
      {selectedMusic && (
        <div
          aria-label="Background music"
          className="preflight pointer-events-auto flex size-12 items-center justify-center rounded-md bg-[#265832]"
        >
          <AudioLinesIcon className="size-9 text-white" />
        </div>
      )}
    </div>
  );
};

export default BackgroundMusicSelector;
